<template>
  <div class="prozess-wrapper">
    <div class="prozess">
      <div>
        <div v-if="!claimed">
          <TaskBlocker :assignee="data.assignee" />
        </div>
        <div v-if="!isLoading">
          <CustomerDetails
            v-if="customerAddress !== null"
            style="margin-top: 15px"
            :data="customerAddress"
            parseSalutation
            title="Kundeninformationen"
          />
          <display-container label="Informationen">
            <div class="details">
              <data-display label="varioResource">
                <template v-if="ontResourceMissing">
                  <v-icon
                    class="mr-2"
                    color="red"
                    small
                  >
                    mdi-close
                  </v-icon>
                  <span>ONT Resource nicht vorhanden</span>
                </template>
                <template v-else>
                  <v-icon
                    class="mr-2"
                    color="green"
                    small
                  >
                    mdi-check
                  </v-icon>
                  <span>ONT Resource vorhanden</span>
                </template>
              </data-display>
              <data-display label="Nächste automatische Überprüfung">
                <template v-if="nextScheduledJob !== null">
                  {{ dueDate }}
                </template>
                <template v-else>
                  -
                </template>
              </data-display>
            </div>
          </display-container>
          <MemoField
            v-model="memo"
            :disabled="!claimed"
            :counter="60"
          />
        </div>
        <div v-else class="my-8">
          <span>Daten werden geladen</span>
          <v-progress-linear
            color="blue accent-4"
            indeterminate
            rounded
            height="6"
          ></v-progress-linear>
        </div>
        <div v-if="!isLoading" class="margin-bottom-2 align-buttons-right">
          <v-btn
            color="secondary"
            :disabled="!claimed || !memoIsValid"
            @click="saveMemo()"
          >
            Memo Speichern
          </v-btn>
          <v-btn
            :disabled="!claimed"
            color="primary"
            @click="completeTaskDialog = true"
          >
            Aufgabe schließen
          </v-btn>
        </div>
      </div>
      <v-dialog v-model="completeTaskDialog" width="20vw">
        <v-card>
          <v-card-text class="pt-6">
            <p>
              Soll die Aufgabe beendet werden? Dadurch läuft der Prozess normal
              weiter, es werden keine Daten durch SPOT geändert.
            </p>
            <v-spacer></v-spacer>
            <div class="dialog-buttons">
              <v-btn
                small
                color="secondary"
                class="align-center"
                @click="completeTaskDialog = !completeTaskDialog"
              >
                Abbrechen
              </v-btn>
              <v-btn
                small
                color="primary"
                class="align-center"
                @click="completeTask"
              >
                Ja
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>
<script>
import { HTTP } from '@/main/httpClient.js';
import TimeUtility from '@/util/TimeUtility.js';
import NotificationObject from '@/main/NotificationObject.js';

export default {
  name: 'ActionOntResourceMissing',
  components: {
    TaskBlocker: () => import('@/task/TaskBlocker'),
    DisplayContainer: () => import('@/components/elements/DisplayContainer'),
    DataDisplay: () => import('@/components/elements/DataDisplay.vue'),
    MemoField: () => import('@/components/elements/MemoField.vue'),
    CustomerDetails: () => import('@/components/CustomerDetails.vue')
  },
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    taskId: null,
    isLoading: true,
    completeTaskDialog: false,
    ontResourceMissing: null,
    nextScheduledJob: null,
    customerAddress: null,
    contractId: null,
    customerId: null,
    memo: ''
  }),
  computed: {
    username() {
      return this.$store.getters.getUsername;
    },
    claimed() {
      return this.data.assignee === this.username;
    },
    camundaAdmin() {
      return this.$auth.user().userRoles.includes('camunda-admin');
    },
    memoIsValid() {
      return this.memo == null || this.memo.length <= 60;
    },
    dueDate() {
      if (this.nextScheduledJob === null) return -1;
      return TimeUtility.formatDateTime(this.nextScheduledJob.dueDate);
    }
  },
  watch: {
    data: {
      deep: true,
      immediate: true,
      handler: async function (newVal) {
        if (this.taskId === null || this.taskId !== newVal.id) {
          this.taskId = newVal.id;
          this.isLoading = true;
          await new Promise((resolve) => setTimeout(resolve, 1));
          await Promise.all([
            this.loadTaskVariables(),
            this.loadOntResourceMissingDetails()
          ]);
          this.isLoading = false;
        }
      }
    }
  },
  methods: {
    loadTaskVariables: function () {
      return HTTP.get(`/task/${this.data.id}/variables`)
        .then((response) => {
          // Prefer local customer data to process customer data
          this.customerId = response.data.customerId.value;
          this.contractId = response.data.contractId.value;
          if (response.data.memo !== undefined) {
            this.memo = response.data.memo.value;
          }
          return this.fetchAddressInfo(this.contractId);
        })
        .catch((response) => {
          this.showToast(
            'error',
            `Error loading task variables HTTP Status Code ${response.status}`
          );
        });
    },
    fetchAddressInfo(contractId) {
      return HTTP.get(`/customer/contactAddress/${contractId}`)
        .then((res) => {
          this.customerAddress = { ...res.data, customerId: this.customerId };
        })
        .catch(() => {
          this.showToast(
            'error',
            'Die Kundendaten konnten nicht geladen werden!'
          );
        });
    },
    loadOntResourceMissingDetails: function () {
      return HTTP.get('/orderTask/ontResourceMissingDetails/' + this.data.id)
        .then(({ data }) => {
          this.nextScheduledJob =
            data.jobs && data.jobs.length > 0 ? data.jobs[0] : null;
          this.ontResourceMissing = data.ontResourceMissing;
        })
        .catch(({ response }) => {
          this.showToast(
            'error',
            `Fehler beim laden der Prozessinformationen HTTP Status Code ${response.status}`
          );
        });
    },
    saveMemo: function () {
      HTTP.post(`/task/${this.data.id}/localVariables`, {
        modifications: {
          memo: {
            value: this.memo,
            type: 'String'
          }
        }
      }).then(() => {
        this.showToast('success', 'Memo gespeichert');
      });
    },
    showToast: function (type, message) {
      this.$store.commit(
        'addNotification',
        new NotificationObject(type, message)
      );
    },
    parseDate: function (date) {
      return new Date(Date.parse(date));
    },
    convertDate: function (date) {
      if (!date) return '-';
      return new Date(Date.parse(date)).toLocaleDateString('de-DE');
    },
    completeTask: function () {
      HTTP.post(`/engine/default/task/${this.data.id}/complete`, {
        ontResourceMissingTask: this.username
      })
        .then(() => {
          this.$store.commit('triggerTasksRefresh');
          this.$emit('onClose');
        })
        .catch(() =>
          this.showToast(
            'error',
            'Die Aufgabe konnte nicht erfolgreich abgeschlossen werden!'
          )
        );
    }
  }
};
</script>

<style scoped>
.details {
  display: grid;
  grid-template-columns: calc(50% - var(--goe-spacing-0_5)) calc(
      50% - var(--goe-spacing-0_5)
    );
  grid-gap: var(--goe-spacing-1);
}

.dialog-buttons :first-child {
  margin-right: 5px;
}
</style>
